import { action, observable, computed } from 'mobx';
import { RootStore } from './rootStore';
import { Flow, Flows, DetailPage } from 'pages/Accounts/Detail/enums';
import { AccountStatus, ActiveStatus, TransferOption } from 'enums';
import { AccountDetailStore } from 'stores/account/accountDetailStore';
import { AccountsStore, overdue, AccountPageTypes } from 'stores/accountsStore';
import { API_VERSION, API_ACCOUNT_HASNOTE, ADMIN_SERVICES } from 'api/endpoints';
import { APP_CONFIG } from 'appConfig';
import { ACCOUNTS_NOTES } from 'routes';

import { realms } from 'helpers/realms';
import { removeItems } from 'helpers/helpers';

interface UserMenuData {
  loaded?: boolean;
  openInfoModal?: (open: boolean) => void;
  openDepositModal?: (open: boolean) => void;
  openClientViewModal?: (open: boolean) => void;
  handlePageChange?: (tab: DetailPage) => void;
  changeFlowTab?: Function;
  handleEdit?: (flow: Flows, tab?: DetailPage) => void;
  editMode?: boolean;
  isEditDisabled?: boolean;
  status?: AccountStatus;
  activePage?: DetailPage;
  //activeSection?: DetailSection;
  activeFlow?: Flow | null;
  clientViewEnabled?: boolean;
  questionnaireOverdue?: boolean;
  processingChange?: boolean;
  locked?: boolean;
  activeStatus?: ActiveStatus;
  transferOption?: TransferOption;
  profileLockActive?: boolean;
}

export enum ServiceStatus {
  'OK' = 'OK',
  'DOWN' = 'DOWN',
}

interface ServiceDef {
  realms: string[];
}

export interface ServiceMessage {
  id: string;
  status: ServiceStatus;
}

const allRealms = [...new Set(Object.values(realms))];

export const services: { [key: string]: ServiceDef } = {
  app: { realms: allRealms },
  auth: { realms: allRealms },
  rabbitPublisher: { realms: removeItems(allRealms, ['bt', 'endusers']) },
  gotenberg: { realms: allRealms },
  ocp: { realms: allRealms },
  bopo: { realms: ['core', 'bt'] },
  businessCase: { realms: ['core'] },
  clientCards: { realms: ['core'] },
};

export type ServicesResponse = { [key: string]: { status: ServiceStatus } };

interface ServiceHistory {
  service: string;
  since: string;
  until: string;
}

interface AccountsListData {
  newAccountsStore?: AccountsStore;
  tab: AccountPageTypes;
}

export class UIStore {
  private rootStore: RootStore;

  @observable version = '';

  @observable loadingText: string | undefined = undefined;
  private loadQueue: { [key: string]: boolean } = {};
  @observable loadingQueueCount = 0;
  @observable loadingCount = 0;

  @observable helpDialogShown = false;
  @observable detail = false;
  @observable userMenuData: UserMenuData = {};
  @observable detailMenuOpen?: boolean = undefined;

  @observable accountsListData: AccountsListData | null = null;
  @observable currentAccountId: string | undefined = undefined;
  @observable currentAccountNotes: boolean | null = null;

  @observable serviceStatus: ServiceMessage[] = [];
  @observable serviceHistory: ServiceHistory[] = [];

  private notesWindow: Window | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    window.addEventListener('message', this.handleEvents);
  }

  @action setDetail = (detail: boolean) => (this.detail = detail);
  @action setVersion = (ver: string) => (this.version = ver);
  @action setDetailMenuOpen = (open: boolean) => (this.detailMenuOpen = open);

  @action
  fetchVersion = async () => {
    const response = await this.rootStore.stores.apiStore.get<{ version: string }>(API_VERSION);
    if (response?.data) {
      this.setVersion(`FE: ${APP_CONFIG.appVersion || 'n/a'} | BE: ${response.data.version || 'n/a'}`);
    }
  };

  @action
  setLoading = (isLoading: boolean): void => {
    this.loadingCount += isLoading ? 1 : -1;
    if (this.loadingCount <= 0) this.loadingCount = 0;
  };

  @action startLoading(id: string, text?: string) {
    if (!this.loadQueue[id]) this.loadQueue[id] = true;
    this.loadingText = text;
    this.loadingQueueCount = Object.keys(this.loadQueue).length;
  }

  @action stopLoading(id: string) {
    if (this.loadQueue[id]) delete this.loadQueue[id];
    this.loadingText = undefined;
    this.loadingQueueCount = Object.keys(this.loadQueue).length;
  }

  @computed
  get loading(): boolean {
    return this.loadingCount > 0 || this.loadingQueueCount > 0;
  }

  @action
  setHelpDialogShown = (shown: boolean) => {
    this.helpDialogShown = shown;
  };

  @action
  setUserMenuData = (data: {
    store: AccountDetailStore;
    openInfoModal?: (open: boolean) => void;
    openDepositModal?: (open: boolean) => void;
    openClientViewModal?: (open: boolean) => void;
  }) => {
    // FIXME: DRF check if all needed
    this.userMenuData.loaded = !!data.store.data.summary;

    this.userMenuData.activePage = data.store.currentPage;
    this.userMenuData.handlePageChange = data.store.setPage;
    this.userMenuData.changeFlowTab = data.store.tabChange;
    this.userMenuData.status = data.store.status;
    this.userMenuData.activeStatus = data.store.activeStatus;
    this.userMenuData.handleEdit = data.store.edit;
    this.userMenuData.editMode = data.store.editMode;
    this.userMenuData.isEditDisabled = data.store.isEditDisabled;
    //this.userMenuData.activeSection = data.store.section; // FIXME: DRF remove
    this.userMenuData.activeFlow = data.store.flow;
    this.userMenuData.processingChange = data.store.processing;

    const reactivationEnabled = data.store.data.summary?.ownerData.reactivationEnabled;
    const resetEnabled = data.store.data.summary?.ownerData.resetEnabled;
    const changeClientViewEnabled = data.store.data.summary?.ownerData.changeClientViewEnabled;
    this.userMenuData.clientViewEnabled =
      resetEnabled ||
      (changeClientViewEnabled &&
        (reactivationEnabled ||
          (reactivationEnabled === false && data.store.data.summary?.ownerData.clientView !== undefined)));

    this.userMenuData.openInfoModal = data.openInfoModal;
    this.userMenuData.openDepositModal = data.openDepositModal;
    this.userMenuData.openClientViewModal = data.openClientViewModal;

    this.userMenuData.questionnaireOverdue =
      data.store.data.detail !== null &&
      overdue.includes(data.store.data.detail.investmentQuestionnaireValidityDate.severity);

    this.userMenuData.locked = data.store.locked;
    this.userMenuData.profileLockActive = !!data.store.data.detail?.profileLockActive;
    this.userMenuData.transferOption = data.store.transferOption;
  };

  @action
  setAccountsListData = (data: AccountsListData) => {
    this.accountsListData = data;
  };

  @action
  unsetAccountsListData = () => (this.accountsListData = null);

  setWindowTitle = (title?: string) => {
    document.title = `${title ? `${title} | ` : ''}Edward`;
  };

  @action
  setServiceMessages = (status: ServiceMessage[]) => {
    this.serviceStatus = status;
  };

  @action
  getServicesHistory = async () => {
    const response = await this.rootStore.stores.apiStore.get<ServiceHistory[]>(ADMIN_SERVICES);
    if (response?.data) {
      this.serviceHistory = response.data;
    }
  };

  fetchNoteInDB = async () => {
    if (this.currentAccountId) {
      const response = await this.rootStore.stores.apiStore.get<{ value: boolean }>(
        API_ACCOUNT_HASNOTE(this.currentAccountId),
      );
      if (response?.data) {
        this.currentAccountNotes = response.data.value;
      }
    }
  };

  unsetCurrentAccountId = () => {
    this.currentAccountId = undefined;
    this.currentAccountNotes = null;
  };

  setCurrentAccountId = (accountId: string, userMode?: boolean) => {
    this.currentAccountId = accountId;
    this.currentAccountNotes = null;
    if (!userMode) this.fetchNoteInDB();
  };

  sendLogoutToNotes = () => {
    this.notesWindow && this.notesWindow.postMessage({ call: 'logout' }, window.location.origin);
  };

  sendRefreshToNotes = () => {
    this.notesWindow && this.notesWindow.postMessage({ call: 'refresh' }, window.location.origin);
  };

  handleEvents = (event: MessageEvent) => {
    if (!event.data) return;
    if (event.origin === window.location.origin) {
      if (event.data.call === 'refresh') {
        this.rootStore.stores.userStore.resetSessionTimers();
        this.currentAccountNotes = true;
      }
      if (event.data.call === 'closeNote') {
        // …
      }
      if (event.data.call === 'deleteNote' && event.data.value.accountId) {
        if (this.currentAccountId) {
          this.currentAccountNotes = false;
        } else if (this.accountsListData?.newAccountsStore) {
          this.accountsListData.newAccountsStore.updateNoteStatus(
            this.accountsListData.tab,
            event.data.value.accountId as string,
            false,
          );
        }
      }
      if (event.data.call === 'addNote' && event.data.value.accountId) {
        if (this.currentAccountId) {
          this.currentAccountNotes = true;
        } else if (this.accountsListData?.newAccountsStore) {
          this.accountsListData.newAccountsStore.updateNoteStatus(
            this.accountsListData.tab,
            event.data.value.accountId as string,
            true,
          );
        }
      }
    } else return;
  };

  updateNoteTitle = (title: string) => {
    this.notesWindow && this.notesWindow.postMessage({ call: 'titleNote', value: title }, window.location.origin);
  };

  openNotes = (accountId?: string) => {
    if (this.currentAccountId || accountId) {
      this.notesWindow = window.open(
        ACCOUNTS_NOTES(accountId || this.currentAccountId),
        'accountNotes',
        'popup,left=10,top=10,width=640,height=480',
      );
    }
  };
}
